/* ==========================================================================
   Colourway swatches on collection product cards
   --------------------------------------------------------------------------
   Renders, into every product card on a collection page, a row of colour
   squares — one per colourway of that product — each linking to the sibling
   product for that colourway.

   Data comes from two product metafields:
     custom.colour_name      single_line_text_field   e.g. "Damson"
     custom.sibling_products list.product_reference   the whole colourway family,
                                                      INCLUDING the product itself

   The row is shown to every shopper. To switch it off on collection pages
   without a deploy, set window.PopsyColourways.disabled = true before this
   script runs.

   Hovering a swatch previews that colourway on the card: the card's main
   thumbnail becomes the linked product's first image until the pointer leaves
   the row. Cards only — the product page has its own gallery.

   The product page renders the same markup from Liquid instead — see
   snippets/popsy-colourways-list.liquid. Keep the classes in step. The colours
   live in the CSS so neither renderer has to carry a map. The 8-swatch cap
   below is specific to cards; the product page shows the whole family.

   Why this is JavaScript and not a Liquid block
   ---------------------------------------------
   The main collection templates disable `main-collection` and let the Boost
   app render the grid client-side, so snippets/product-grid-item.liquid never
   runs there and there is no Liquid hook to add a block to. The cards only
   exist in the DOM. So we watch for them and read the metafields from the
   Storefront API — the same approach snippets/boost-currency-bridge.liquid
   already takes for Boost's prices.

   The native Liquid grid is handled too (`.grid-product`), because
   collection.no-sidebar, collection.no-promos and collection.boost-sd-original
   keep `main-collection` enabled and render cards server-side.
   ========================================================================== */

(function () {
  'use strict';

  if (window.__popsyColourways) return;
  window.__popsyColourways = true;

  var config = window.PopsyColourways || {};
  if (config.disabled === true) return;

  var API_VERSION = config.apiVersion || '2025-07';
  var API_TIMEOUT_MS = 6000;

  /* Both grids. Boost re-renders its cards on filter/sort/paginate, which is
     why this is a MutationObserver rather than a one-shot pass. */
  var CARD_SELECTOR = '.boost-sd__product-item[data-product-id], .grid-product[data-product-id]';

  var BATCH_SIZE = 50; // ids per Storefront request (measured cost: 22 points)
  var FAMILY_LIMIT = 50; // siblings resolved per product
  var DEBOUNCE_MS = 60;
  var MIN_SWATCHES = 2; // a family of one is not a colourway choice

  /* At most 8 swatches on a card, so they can be drawn large enough to read
     under a thumbnail. The colourway being viewed is always one of them: if it
     falls outside the first 8 of the merchant's order it takes the last slot,
     because the active ring is the only thing tying the row to the card it sits
     under. The product page has the room and shows the whole family. */
  var MAX_SWATCHES = 8;

  /* Hover preview. The card thumbnail is swapped for the hovered colourway's
     first image; Boost and the native Liquid grid name that image differently.
     We deliberately do NOT touch the card's aspect ratio — both grids size the
     image box from the *original* product, and re-sizing it on hover would jog
     the whole grid. The replacement is `object-fit: cover`d into the same box. */
  var MAIN_IMAGE_SELECTOR =
    'img.boost-sd__product-image-img--main, img.grid-product__image, ' +
    '.boost-sd__product-image img, .grid__item-image-wrapper img';
  var IMAGE_WIDTHS = [200, 300, 400, 500, 600, 700, 800, 900, 1000, 1200];
  var IMAGE_FALLBACK_WIDTH = 600;

  var cache = {}; // product id -> { colour, siblings } or null once known-empty
  var queued = {}; // product id -> [card, ...] awaiting data
  var requested = {}; // product id -> true, so an id is only ever fetched once
  var timer = null;

  function handleize(value) {
    return String(value == null ? '' : value)
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  /* The Storefront API token is published in content_for_header. Read it lazily
     so load order does not matter. */
  function storefrontToken() {
    try {
      var el = document.getElementById('shopify-features');
      return el ? JSON.parse(el.textContent).accessToken : null;
    } catch (e) {
      return null;
    }
  }

  /* Match the links Boost writes: /collections/<handle>/products/<handle>, so a
     swatch keeps the same collection context as the card it sits under. */
  function productUrl(handle) {
    var root = config.rootUrl || '/';
    if (root.charAt(root.length - 1) !== '/') root += '/';
    if (config.collectionHandle) {
      return root + 'collections/' + config.collectionHandle + '/products/' + handle;
    }
    return root + 'products/' + handle;
  }

  var QUERY =
    'query($ids:[ID!]!){nodes(ids:$ids){... on Product{id' +
    ' sp: metafield(namespace:"custom",key:"sibling_products"){' +
    ' references(first:' + FAMILY_LIMIT + '){nodes{... on Product{' +
    ' id handle title availableForSale productType' +
    ' featuredImage{altText width full: url}' +
    ' swatch: images(first:2){nodes{' +
    ' thumb: url(transform:{maxWidth:64,maxHeight:64,crop:CENTER})}}' +
    ' cn: metafield(namespace:"custom",key:"colour_name"){value}' +
    '}}}}}}}';

  /* Which swatch image to use depends on the product type. For CLOTHING it is
     the product's SECOND image (the styled / colour-true shot), falling back to
     the first; for everything else — tights, socks, bags, accessories, homeware,
     shoes — it is the FIRST image. Anything not matched below falls to the first
     image. Keep this split in step with snippets/popsy-colourways-list.liquid.
     The hover preview still uses the featured image regardless. */
  var CLOTHING_TYPES = [
    'dress', 'shirt', 'top', 'trouser', 'jumpsuit', 'romper', 'coat', 'jacket',
    'swimsuit', 'skirt', 'jumper', 'hoodie', 'pinafore', 'lounge', 'children',
    'cardigan', 'blouse', 'tunic', 'playsuit', 'kaftan'
  ];

  function isClothing(productType) {
    var t = String(productType == null ? '' : productType).toLowerCase();
    for (var i = 0; i < CLOTHING_TYPES.length; i++) {
      if (t.indexOf(CLOTHING_TYPES[i]) !== -1) return true;
    }
    return false;
  }

  function swatchImage(ref) {
    var nodes = (ref.swatch && ref.swatch.nodes) || [];
    var pick = isClothing(ref.productType) ? (nodes[1] || nodes[0]) : nodes[0];
    return pick ? pick.thumb : null;
  }

  function fetchBatch(ids) {
    var token = storefrontToken();
    if (!token) return Promise.reject(new Error('no storefront token'));

    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var timeout = setTimeout(function () {
      if (controller) controller.abort();
    }, API_TIMEOUT_MS);

    return fetch('/api/' + API_VERSION + '/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': token
      },
      body: JSON.stringify({
        query: QUERY,
        variables: { ids: ids.map(function (id) { return 'gid://shopify/Product/' + id; }) }
      }),
      signal: controller ? controller.signal : undefined
    })
      .then(function (res) { return res.json(); })
      .then(function (payload) {
        clearTimeout(timeout);
        var nodes = (payload && payload.data && payload.data.nodes) || [];
        nodes.forEach(function (node) {
          if (!node || !node.id) return;
          var id = node.id.split('/').pop();
          var refs = (node.sp && node.sp.references && node.sp.references.nodes) || [];
          cache[id] = refs
            .filter(function (ref) { return ref && ref.handle; })
            .map(function (ref) {
              return {
                id: ref.id.split('/').pop(),
                handle: ref.handle,
                title: ref.title,
                available: ref.availableForSale !== false,
                image: swatchImage(ref),
                photo: ref.featuredImage
                  ? {
                      src: ref.featuredImage.full,
                      width: ref.featuredImage.width,
                      alt: ref.featuredImage.altText || ref.title
                    }
                  : null,
                colour: ref.cn ? ref.cn.value : null
              };
            });
        });
        /* Anything the response did not cover has no data — record that so the
           card is not re-queued on the next mutation. */
        ids.forEach(function (id) {
          if (!cache[id]) cache[id] = [];
        });
      })
      .catch(function (err) {
        clearTimeout(timeout);
        ids.forEach(function (id) { cache[id] = []; });
        if (window.console && console.warn) {
          console.warn('[popsy-colourways] metafield lookup failed', err);
        }
      });
  }

  /* Both halves of the fill are handed to CSS, which decides between them —
     see the colour map in assets/popsy-colourways.css. */
  function swatchFill(el, sibling, classes) {
    var key = handleize(sibling.colour);
    if (key) classes += ' popsy-colourways__swatch--' + key;
    el.className = classes;
    if (sibling.image) {
      el.style.setProperty('--popsy-swatch-image', 'url("' + sibling.image.replace(/"/g, '%22') + '")');
    }
  }

  /* The merchant's first MAX_SWATCHES, with the current product guaranteed a
     place. Cards only — the product page renders every sibling. */
  function visibleSiblings(siblings, id) {
    if (siblings.length <= MAX_SWATCHES) return siblings;
    var shown = siblings.slice(0, MAX_SWATCHES);
    var hasSelf = shown.some(function (s) { return s.id === id; });
    if (!hasSelf) {
      var self = siblings.filter(function (s) { return s.id === id; })[0];
      if (self) shown[MAX_SWATCHES - 1] = self;
    }
    return shown;
  }

  /* ------------------------------------------------------------------ *
     Hover preview
     ------------------------------------------------------------------ */

  /* Shopify's CDN sizes from the URL, so a ladder is just query strings. Stop
     at the source width — asking for more than exists only wastes bytes. */
  function srcsetFor(photo) {
    var sep = photo.src.indexOf('?') === -1 ? '?' : '&';
    var out = [];
    for (var i = 0; i < IMAGE_WIDTHS.length; i++) {
      if (photo.width && IMAGE_WIDTHS[i] > photo.width) break;
      out.push(photo.src + sep + 'width=' + IMAGE_WIDTHS[i] + ' ' + IMAGE_WIDTHS[i] + 'w');
    }
    if (!out.length) {
      var w = photo.width || IMAGE_FALLBACK_WIDTH;
      out.push(photo.src + sep + 'width=' + w + ' ' + w + 'w');
    }
    return out.join(', ');
  }

  function snapshot(img) {
    return {
      src: img.getAttribute('src'),
      srcset: img.getAttribute('srcset'),
      alt: img.getAttribute('alt')
    };
  }

  function applyImage(img, state) {
    ['src', 'srcset', 'alt'].forEach(function (attr) {
      if (state[attr] == null) img.removeAttribute(attr);
      else img.setAttribute(attr, state[attr]);
    });
  }

  function preview(card, sibling) {
    var img = card.querySelector(MAIN_IMAGE_SELECTOR);
    if (!img || !sibling || !sibling.photo || !sibling.photo.src) return;

    /* Every hover bumps the token. A slow image that finishes downloading after
       the pointer has moved on finds its token stale and does nothing. */
    var token = (card.__popsyHover || 0) + 1;
    card.__popsyHover = token;

    var sep = sibling.photo.src.indexOf('?') === -1 ? '?' : '&';
    var next = {
      src: sibling.photo.src + sep + 'width=' + IMAGE_FALLBACK_WIDTH,
      srcset: srcsetFor(sibling.photo),
      alt: sibling.photo.alt || sibling.title || null
    };

    /* Load first, swap second, so the card never flashes empty — the thumbnail
       holds the current colourway until the next one can actually be painted. */
    var loader = new Image();
    loader.sizes = img.getAttribute('sizes') || '';
    loader.srcset = next.srcset;
    loader.src = next.src;

    function show() {
      if (card.__popsyHover !== token) return;
      if (!card.__popsyRestore) card.__popsyRestore = snapshot(img);
      applyImage(img, next);
      card.classList.add('popsy-colourways-previewing');
    }

    if (loader.complete) show();
    else loader.addEventListener('load', show);
  }

  function endPreview(card) {
    card.__popsyHover = (card.__popsyHover || 0) + 1;
    card.classList.remove('popsy-colourways-previewing');
    var saved = card.__popsyRestore;
    if (!saved) return;
    card.__popsyRestore = null;
    var img = card.querySelector(MAIN_IMAGE_SELECTOR);
    if (img) applyImage(img, saved);
  }

  /* Delegated from the row, not bound per swatch: mouseenter does not bubble,
     so this is mouseover/mouseout with the usual child-boundary guards.

     Bound unconditionally rather than behind a `(hover: hover)` check. A swatch
     is a link, so the single mouseover a tap emits is followed by navigation
     away — there is nothing to leave stranded — and the media query is not
     dependable enough to gate on: headless Chrome reports `(hover: none)`,
     which silently disabled this the first time round. */
  function bindPreview(wrap, card) {
    function swatchFrom(node) {
      return node && node.closest ? node.closest('.popsy-colourways__swatch') : null;
    }

    wrap.addEventListener('mouseover', function (event) {
      var swatch = swatchFrom(event.target);
      if (!swatch || swatch === wrap.__popsyActive) return;
      wrap.__popsyActive = swatch;
      preview(card, swatch.__popsySibling);
    });

    wrap.addEventListener('mouseout', function (event) {
      var swatch = swatchFrom(event.target);
      if (!swatch) return;
      var to = event.relatedTarget;
      /* Still inside the same swatch, or on the way to another one — in the
         latter case the matching mouseover is about to take over. */
      if (to && (swatch.contains(to) || swatchFrom(to))) return;
      wrap.__popsyActive = null;
      endPreview(card);
    });

    /* Safety net for the cases mouseout misses: leaving the window, or the
       pointer jumping straight out of the row. */
    wrap.addEventListener('mouseleave', function () {
      wrap.__popsyActive = null;
      endPreview(card);
    });
  }

  function render(card, id) {
    var all = cache[id];
    if (!all || all.length < MIN_SWATCHES) return;
    if (card.querySelector('.popsy-colourways')) return;
    var siblings = visibleSiblings(all, id);

    var wrap = document.createElement('div');
    wrap.className = 'popsy-colourways';
    wrap.setAttribute('data-colourway-count', String(all.length));

    var list = document.createElement('ul');
    list.className = 'popsy-colourways__list';
    list.setAttribute('aria-label', 'Other colours');

    siblings.forEach(function (sibling) {
      var item = document.createElement('li');
      item.className = 'popsy-colourways__item';

      var link = document.createElement('a');
      var classes = 'popsy-colourways__swatch';
      if (sibling.id === id) classes += ' popsy-colourways__swatch--active';
      if (!sibling.available) classes += ' popsy-colourways__swatch--sold-out';
      link.href = productUrl(sibling.handle);
      link.setAttribute('data-colour', sibling.colour || '');
      if (sibling.id === id) link.setAttribute('aria-current', 'page');

      swatchFill(link, sibling, classes);
      link.__popsySibling = sibling;

      var label = document.createElement('span');
      label.className = 'visually-hidden';
      label.textContent = sibling.colour || sibling.title;
      link.appendChild(label);

      item.appendChild(link);
      list.appendChild(item);
    });

    wrap.appendChild(list);
    card.appendChild(wrap);

    bindPreview(wrap, card);
  }

  function flush() {
    timer = null;
    var ids = Object.keys(queued).filter(function (id) { return !requested[id]; });
    var ready = Object.keys(queued).filter(function (id) { return cache[id]; });

    ready.forEach(function (id) {
      queued[id].forEach(function (card) { render(card, id); });
      delete queued[id];
    });

    if (!ids.length) return;
    ids.forEach(function (id) { requested[id] = true; });

    for (var i = 0; i < ids.length; i += BATCH_SIZE) {
      (function (chunk) {
        fetchBatch(chunk).then(function () {
          chunk.forEach(function (id) {
            if (!queued[id]) return;
            queued[id].forEach(function (card) { render(card, id); });
            delete queued[id];
          });
        });
      })(ids.slice(i, i + BATCH_SIZE));
    }
  }

  function claim(card) {
    var id = card.getAttribute('data-product-id');
    if (!id || card.getAttribute('data-popsy-colourways') === 'done') return;
    card.setAttribute('data-popsy-colourways', 'done');

    if (cache[id]) {
      render(card, id);
      return;
    }
    if (!queued[id]) queued[id] = [];
    queued[id].push(card);

    if (timer) clearTimeout(timer);
    timer = setTimeout(flush, DEBOUNCE_MS);
  }

  function scan(root) {
    if (root.nodeType !== 1) return;
    if (root.matches && root.matches(CARD_SELECTOR)) claim(root);
    var found = root.querySelectorAll ? root.querySelectorAll(CARD_SELECTOR) : [];
    for (var i = 0; i < found.length; i++) claim(found[i]);
  }

  function start() {
    scan(document.body);
    new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) scan(added[j]);
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
